import React, { useEffect, useState } from "react";
import { resourceLines } from "app/utils/request/scheduling/facilityResources";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import { DateTime } from "luxon";
import { schedulingFetchPost } from "app/utils/request/scheduling/fetch";
import { setBookSession } from "app/actions/scheduling/bookSession";
import InfoModal from "app/routes/Calendar/InfoModal";
import PanelWrapper from "app/components/PanelWrapper";
import PublicWrapper from "app/components/PublicWrapper";
import { Spinner } from "app/components/Loading";
import settings from "config/environment";

interface PurchaseConfirmationParams {
  id: string;
}

const PurchaseConfirmation: React.FC = () => {
  const { id } = useParams<PurchaseConfirmationParams>();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);
  const isMobile = ["true"].includes((searchParams.get("mobile") || "").toLowerCase());

  const [error, setError] = useState<string | null>(null);
  const [attemptedDeepLink, setAttemptedDeepLink] = useState(false);
  const [deepLinkUrl, setDeepLinkUrl] = useState<string | null>(null);
  const [hasFinalized, setHasFinalized] = useState(false);

  useEffect(() => {
    const finalizePurchase = async () => {
      try {
        // Call finalize API to complete the purchase
        const response = await schedulingFetchPost(`/stripe_purchases/${id}/finalize`, {});
        const { programIds, coachProfileId, purchaseItems } = response;
        const itemType = purchaseItems?.[0]?.type;

        // Determine redirect path and mobile type based on purchase item type
        let pathWeb: string;
        let mobileType: string;

        if (itemType === "program_registration") {
          const programId = programIds?.[0] || purchaseItems?.[0]?.details?.programId;
          pathWeb = programId ? `/programs/${programId}/view?registration_success=true` : `/programs`;
          mobileType = "program";
        } else if (itemType === "time_slot_reservation" || itemType === "lesson_schedule") {
          // Populate bookSession state for the success modal
          const details = purchaseItems?.[0]?.details || {};
          const coach = details.coach || {};
          const firstName = coach.firstName || coach.first_name || '';
          const lastName = coach.lastName || coach.last_name || '';
          const startTime = details.startTime || details.start_time;
          const timeZone = details.timeZone || details.time_zone || 'UTC';

          dispatch(setBookSession({
            coach: {
              fullName: [firstName, lastName].filter(Boolean).join(' '),
              profilePicture: coach.profilePicture || coach.profile_picture,
            },
            date: startTime,
            time: {
              startHour: startTime ? DateTime.fromISO(startTime, { zone: timeZone }).toMillis() : null,
              timeZone,
            },
            resourceLines: resourceLines(details),
          }));

          pathWeb = `/sessions?session_booked_modal=true`;
          mobileType = "session";
        } else if (itemType === "stripe_product") {
          const productTitle = purchaseItems?.[0]?.details?.productTitle || '';
          const productType = purchaseItems?.[0]?.details?.productType || '';
          const titleParam = productTitle ? `&title=${encodeURIComponent(productTitle)}` : '';
          const typeParam = productType === 'subscription' ? '&offering_type=subscription' : '';
          const returnPath = sessionStorage.getItem("purchase_return_path");
          sessionStorage.removeItem("purchase_return_path");

          if (returnPath) {
            const sep = returnPath.includes("?") ? "&" : "?";
            pathWeb = `${returnPath}${sep}payment=success${titleParam}${typeParam}`;
          } else if (coachProfileId) {
            pathWeb = `/home/${coachProfileId}?payment=success${titleParam}${typeParam}`;
          } else {
            // Facility-level purchase - redirect to sessions with success modal
            pathWeb = `/sessions?payment=success${titleParam}${typeParam}`;
          }
          mobileType = "offering";
        } else {
          pathWeb = `/`;
          mobileType = "purchase";
        }

        console.log("Confirmation redirect - itemType:", itemType, "pathWeb:", pathWeb);

        if (isMobile) {
          const qs = new URLSearchParams({
            purchase_id: String(id ?? ""),
            type: mobileType,
          });
          const deepLink = `${settings.MOBILE_URL}/purchase/success?${qs.toString()}`;

          setDeepLinkUrl(deepLink);

          if (!attemptedDeepLink) {
            setAttemptedDeepLink(true);
            window.location.href = deepLink;
          }
          return;
        }

        history.replace(pathWeb);
      } catch (err) {
        console.error("Error finalizing purchase:", err);

        const error = err as any;
        if (error.status === 409) {
          // Program is full, refund initiated - use backend message
          const backendMessage = error.errors?.error;
          setError(backendMessage || t("Program is now full. A refund has been initiated."));
        } else if (error.status === 422) {
          // Payment not completed
          setError(t("Payment was not completed. Please try again."));
        } else {
          // Other errors
          setError(t("An error occurred while completing your purchase. Please contact support."));
        }
      }
    };

    if (id && !hasFinalized) {
      setHasFinalized(true);
      finalizePurchase();
    }
  }, [id]);

  return (
    <PublicWrapper>
      <PanelWrapper>
        {!attemptedDeepLink && !error && (
          <div className="text-center mt-10 flex flex-col items-center gap-4">
            <div>{t("Processing your payment, please wait...")}</div>
            <br />
            <Spinner />
          </div>
        )}

        {attemptedDeepLink && !error && (
          <div className="text-center mt-10 flex flex-col items-center gap-4 px-4">
            <div className="text-xl font-semibold">{t("Launching App...")}</div>
            <Spinner />
            {deepLinkUrl && (
              <div>
                <Trans i18nKey="paymentsRedirect">
                  If nothing happens, tap <a href={deepLinkUrl}>here</a> to continue
                </Trans>
              </div>
            )}
          </div>
        )}

        {error && (
          <InfoModal
            isOpen
            title={t("Payment Error")}
            status="error"
            onDismiss={() => history.push("/")}
          >
            <div className="flex flex-col items-center">
              <div>{error}</div>
            </div>
          </InfoModal>
        )}
      </PanelWrapper>
    </PublicWrapper>
  );
};

export default PurchaseConfirmation;
